import PropTypes from "prop-types";
import Modal from "@components/ui-components/modal";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import InvoiceDocument from "@panel/shared/invoice/components/invoice-document";
import { normalizeInvoiceDocument } from "@panel/shared/invoice/misc/helpers";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatCurrency } from "@src/utility/helpers/utils";

const InvoiceDetailModal = ({ open, onClose, invoice }) => {
    if (!invoice) return null;

    const isPaid = invoice.status === "paid";
    const doc = normalizeInvoiceDocument(invoice);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={invoice.invoice_number}
            subtitle={`${invoice.account_name} · ${invoice.payout_period ? formatDate(invoice.payout_period) : "—"}`}
            width="lg">
            <div className="max-h-[70vh] space-y-4 overflow-y-auto text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-muted">Status</span>
                    <StatusChip tone={INVOICE_STATUS_TONE[invoice.status] || "neutral"} label={INVOICE_STATUS_LABEL[invoice.status] || invoice.status} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase text-muted">Debit note</p>
                        <p className="mt-1 font-medium text-gray-900">{invoice.debit_note_number || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-muted">Description</p>
                        <p className="mt-1 text-gray-900">{invoice.description || "—"}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-muted">Issue date</p>
                        <p className="mt-1 text-gray-900">{formatDate(invoice.issue_date)}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-muted">Due date</p>
                        <p className="mt-1 text-gray-900">{formatDate(invoice.due_date)}</p>
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-light p-3">
                    <div className="flex items-center justify-between py-1">
                        <span className="text-muted">Subtotal</span>
                        <span className="text-gray-900">{formatCurrency(invoice.subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                        <span className="text-muted">Fee</span>
                        <span className="text-gray-900">{formatCurrency(invoice.fee)}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                        <span className="text-muted">Tax</span>
                        <span className="text-gray-900">{formatCurrency(invoice.tax)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 py-1 pt-2 font-semibold">
                        <span className="text-gray-900">Amount</span>
                        <span className="text-gray-900">{formatCurrency(invoice.amount)}</span>
                    </div>
                </div>

                {isPaid && (
                    <div className="rounded-lg border border-success/30 bg-success/5 p-3">
                        <p className="text-xs font-semibold uppercase text-muted">Payment</p>
                        <p className="mt-1 text-gray-900">
                            {invoice.payment_reference || "—"} · {invoice.payment_mode || "—"} · {formatDate(invoice.paid_at)}
                        </p>
                    </div>
                )}

                <InvoiceDocument doc={doc} />
            </div>
        </Modal>
    );
};

InvoiceDetailModal.propTypes = {
    open   : PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    invoice: PropTypes.object
};

export default InvoiceDetailModal;
